import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Divider, IconButton, Modal, Typography, useTheme } from '@mui/material';

import { StandardSmallModalBox } from '@frontend/features/common/utils/modals';
import { QueryKeys } from '@frontend/features/common/types';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

interface DeleteSupportFormProps {
  open: boolean;
  onClose: () => void;
  name?: string;
  id?: string;
}

export const DeleteSupportForm = ({ open, onClose, id, name }: DeleteSupportFormProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();


  const onDelete = async () => {
    try {
      await client.delete(`/case-support-form/${id}`);
      addToast({
        message: t('supportForms.list.deleted'),
        color: 'success',
      });
      queryClient.invalidateQueries(QueryKeys.getSupportFormsByAgency);
    } catch {
      addToast({
        message: t('supportForms.list.deleteError'),
        color: 'error',
      });
    } finally {
      onClose();
    }
  };

  return (
    <Modal open={Boolean(id) && open} onClose={onClose} aria-labelledby={t('supportForms.list.delete', { name })}>
      <StandardSmallModalBox display="flex" flexDirection="column" gap={theme.spacing(2)}>
        <Box display="flex" gap={theme.spacing(2)}>
          <Box flexGrow={1} display="flex" alignItems="center" gap={theme.spacing(1)}>
            <PersonIcon color="disabled" />
            <Typography id="delete-image-tag-modal-title" variant="h6" component="h2">
              {t('supportForms.list.deleteTitle')}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </Box>
        <Divider />
        <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
          <Typography variant="body2">{t('supportForms.list.delete', { name })}</Typography>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-evenly"
            width="100%"
            gap={theme.spacing(2)}
            sx={{
              '& > button': {
                width: 'calc(50% - 4px)',
              },
            }}
          >
            <Button variant="outlined" color="error" size="medium" onClick={onClose}>
              {t('common.form.discard')}
            </Button>
            <Button variant="contained" color="primary" size="medium" onClick={onDelete}>
              {t('common.form.delete')}
            </Button>
          </Box>
        </Box>
      </StandardSmallModalBox>
    </Modal>
  );
};

